import React, { useState, useEffect } from 'react';
import { progressService, JourneyProgress, ProgressItem } from '../services/progressService';
import styles from '../styles/journeyOverview.module.css';

interface JourneyOverviewProps {
  user?: any;
  stepProgress?: Record<number, ProgressItem | null>;
}

const JourneyOverview: React.FC<JourneyOverviewProps> = ({ user, stepProgress }) => {
  const [journeyData, setJourneyData] = useState<JourneyProgress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadJourneyData();
    }
  }, [user]);

  const loadJourneyData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await progressService.getJourneyProgress();
      setJourneyData(data);
    } catch (err) {
      console.error('Error loading journey data:', err);
      setError('Failed to load journey data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.loading}>Loading journey data...</div>
      </div>
    );
  }

  if (error) {
    return null; // Don't show anything if there's an error
  }

  if (!journeyData) {
    return null; // Don't show anything if no data
  }

  if (journeyData.opted_out) {
    return null; // Don't show anything if user has opted out
  }

  // Function to determine step status
  const getStepStatus = (stepNumber: number): 'not-started' | 'in-progress' | 'completed' => {
    if (!user || !stepProgress) return 'not-started';
    
    const progress = stepProgress[stepNumber];
    if (!progress) return 'not-started';
    
    if (progress.completed) return 'completed';
    
    // Check for completed actions
    if (progress.data && progress.data.actions && Array.isArray(progress.data.actions)) {
      const hasCompletedAction = progress.data.actions.some((action: any) => action.completed);
      if (hasCompletedAction) return 'in-progress';
    }
    
    return 'not-started';
  };

  // Find the highest numbered step that has been worked on
  const getHighestWorkedStep = (): { stepNumber: number; status: 'completed' | 'in-progress' } | null => {
    if (!stepProgress) return null;
    
    let highestStep = 0;
    let highestStatus: 'completed' | 'in-progress' = 'not-started' as any;
    
    for (let step = 1; step <= 12; step++) {
      const status = getStepStatus(step);
      if (status === 'completed' || status === 'in-progress') {
        highestStep = step;
        highestStatus = status;
      }
    }
    
    return highestStep > 0 ? { stepNumber: highestStep, status: highestStatus } : null;
  };

  // Check if all 12 steps are completed
  const allStepsCompleted = () => {
    if (!stepProgress) return false;
    for (let step = 1; step <= 12; step++) {
      if (getStepStatus(step) !== 'completed') return false;
    }
    return true;
  };

  // Check if any steps have been started
  const anyStepsStarted = () => {
    if (!stepProgress) return false;
    for (let step = 1; step <= 12; step++) {
      if (getStepStatus(step) !== 'not-started') return true;
    }
    return false;
  };

  // Determine which message to show
  if (allStepsCompleted()) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.journeyMessage}>
          Wow! Congratulations for working through all 12 steps of SAA!
        </div>
      </div>
    );
  } else if (!anyStepsStarted()) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.encouragingMessage}>
          You can do this! Click on Step 1 below to start working the steps
        </div>
      </div>
    );
  } else {
    const highestWorked = getHighestWorkedStep();
    if (highestWorked) {
      if (highestWorked.status === 'completed') {
        const nextStep = highestWorked.stepNumber + 1;
        return (
          <div className={styles.journeyOverview}>
            <div className={styles.journeyMessage}>
              Congratulations on completing Step {highestWorked.stepNumber}! Keep going. Click on Step {nextStep} below to continue your progress
            </div>
          </div>
        );
      } else {
        return (
          <div className={styles.journeyOverview}>
            <div className={styles.journeyMessage}>
              You are making great progress - keep going! Click on Step {highestWorked.stepNumber} to continue your efforts
            </div>
          </div>
        );
      }
    }
    
    // Fallback message
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.journeyMessage}>
          You have started working the 12 steps. Keep going!
        </div>
      </div>
    );
  }
};

export default JourneyOverview; 